/**
 * ReportFrame — the page every report sits in (roadmap RP-7).
 *
 * One question at the top in the display face, the shared controls under it
 * (date range or cycle, segment), and the shared actions on the right: export
 * (CSV, print view for PDF, every chart as PNG), a named snapshot, and a
 * scheduled digest. The controls live in the URL through useReportControls,
 * so the print view, a snapshot and a digest all carry them.
 *
 * A report page is: `<ReportFrame …><Body /></ReportFrame>`. The print route
 * renders the same body inside the print shell instead.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, CalendarClock } from 'lucide-react';
import { Field, Modal, Select, TextArea, TextInput } from '../../../ds';
import { btnPrimary, btnSecondary } from '../../../mono/primitives';
import { ExportMenu } from '../../../ui/ExportMenu';
import { useUIStore } from '../../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { createReportSnapshot, snapshotPath, type ReportSnapshotConfig, type ReportSnapshotSummary } from '../../../../lib/reportSnapshotsService';
import { RANGE_OPTIONS, type RangeKey } from './reportRange';
import { SEGMENT_OPTIONS, segmentDefinition, type SegmentKey } from './reportSegment';
import type { ReportControls } from './useReportControls';
import { downloadChartPng, slugifyFilename } from './exportChartPng';
import { ScheduleReportDialog } from './ScheduleReportDialog';

export interface ReportFrameProps {
  /** Registry key, or `custom:<savedReportId>` for a saved query. */
  reportKey: string;
  /** The live path, no query string. */
  reportPath: string;
  /** Short name for the breadcrumb, the snapshot and the digest. */
  name: string;
  /** The one question this report answers — the heading. */
  question: string;
  description?: ReactNode;
  controls: ReportControls;
  /** Hide the segment control for a report whose rows are not items. */
  showSegment?: boolean;
  /** One sentence under the controls, e.g. which date the window measures. */
  rangeNote?: string;
  /** CSV rows for the export; omit to hide CSV. */
  csv?: { filename: string; rows: () => Record<string, unknown>[] };
  /** The numbers to save in a snapshot or send in a digest; omit to hide both. */
  summary?: () => ReportSnapshotSummary;
  /** Why snapshot, schedule and print are unavailable right now, when they are. */
  unavailableReason?: string | null;
  statement?: string | null;
  savedReportId?: string | null;
  /** The saved report is kept to the person viewing it: a snapshot or a digest still goes further. */
  justMe?: boolean;
  /** Extra controls on the left of the control row (a filter button, say). */
  controlsExtra?: ReactNode;
  /** Extra actions on the right (Save, for a custom report). */
  actionsExtra?: ReactNode;
  /** Replace the heading row's title with something editable. */
  titleSlot?: ReactNode;
  children: ReactNode;
}

export function ReportFrame({
  reportKey,
  reportPath,
  name,
  question,
  description,
  controls,
  showSegment = true,
  rangeNote,
  csv,
  summary,
  unavailableReason = null,
  statement = null,
  savedReportId = null,
  justMe = false,
  controlsExtra,
  actionsExtra,
  titleSlot,
  children,
}: ReportFrameProps) {
  const navigate = useNavigate();
  const addNotification = useUIStore((s) => s.addNotification);
  // Export is for people who hold analytics.export (§2.8: a control the person may
  // not use is not drawn). Fail-open while permissions load so a legitimate user is
  // never blocked mid-load. The server check stays the authority.
  const can = useUserStore((s) => s.can);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const canExport = !permissionsLoaded || can('analytics.export');
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotNote, setSnapshotNote] = useState('');
  const [saving, setSaving] = useState(false);

  const { range, resolved, setRange, segment, setSegment, cycles, search } = controls;
  const segmentLabel = segmentDefinition(segment)?.label ?? null;
  const printHref = `${reportPath}/print${search ? `?${search}` : ''}`;

  const config = useMemo<ReportSnapshotConfig>(
    () => ({ search, rangeLabel: resolved.label, segmentLabel, reportPath, reportName: name, savedReportId, statement }),
    [search, resolved.label, segmentLabel, reportPath, name, savedReportId, statement],
  );

  const canPersist = !!summary && !unavailableReason;

  const handleAllPng = useCallback(async () => {
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-report-chart]'));
    let n = 0;
    for (const card of cards) {
      const title = card.getAttribute('data-chart-title') ?? `chart-${n + 1}`;
      const ok = await downloadChartPng(card, `${slugifyFilename(name)}-${slugifyFilename(title)}`);
      if (ok) n++;
    }
    addNotification({
      type: n > 0 ? 'success' : 'warning',
      title: n > 0 ? 'Charts downloaded' : 'No charts to download',
      message: n > 0 ? `${n} chart${n === 1 ? '' : 's'} saved as PNG.` : 'This report has no chart on the page right now.',
      duration: 3000,
    });
  }, [name, addNotification]);

  const openPrint = useCallback(() => {
    window.open(printHref, '_blank', 'noopener');
  }, [printHref]);

  const handleSnapshot = useCallback(async () => {
    if (!summary) return;
    const trimmed = snapshotName.trim();
    if (!trimmed) return;
    setSaving(true);
    const { snapshot, error } = await createReportSnapshot({ reportKey, name: trimmed, note: snapshotNote.trim() || undefined, config, summary: summary() });
    setSaving(false);
    if (!snapshot) {
      addNotification({ type: 'error', title: 'Snapshot not saved', message: error ?? 'The snapshot could not be saved.', duration: 6000 });
      return;
    }
    setSnapshotOpen(false);
    setSnapshotName('');
    setSnapshotNote('');
    addNotification({ type: 'success', title: 'Snapshot saved', message: `"${snapshot.name}" has its own address now. Anyone signed in to this workspace can open it.`, duration: 4000 });
    navigate(snapshotPath(snapshot.id));
  }, [summary, snapshotName, snapshotNote, reportKey, config, addNotification, navigate]);

  const exportExtra = useMemo(
    () => [
      { id: 'print', label: 'Print or save as PDF', onSelect: openPrint },
      { id: 'png', label: 'Every chart as PNG', onSelect: () => void handleAllPng() },
    ],
    [openPrint, handleAllPng],
  );

  return (
    <div className="space-y-5" data-report-key={reportKey}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <nav className="mb-1 flex items-center gap-1 font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[var(--text-ter)]" aria-label="Breadcrumb">
            <Link to="/prioritization/reports" className="hover:text-[var(--text-sec)]">Reports</Link>
            <span aria-hidden="true">›</span>
            <span className="text-[var(--text-sec)]">{name}</span>
          </nav>
          {titleSlot ?? (
            <h1 className="text-[1.75rem] leading-tight text-[var(--text-pri)]" style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}>
              {question}
            </h1>
          )}
          {description && <p className="mt-1 max-w-2xl text-sm text-[var(--text-sec)]">{description}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {actionsExtra}
          {canExport && (
            <ExportMenu
              data={csv ? undefined : []}
              getData={csv ? async () => csv.rows() : undefined}
              filename={csv?.filename ?? slugifyFilename(name)}
              formats={csv ? ['csv'] : []}
              extraItems={exportExtra}
              label="Export"
            />
          )}
          {summary && (
            <>
              <button type="button" {...btnSecondary()} onClick={() => setSnapshotOpen(true)} disabled={!canPersist} title={unavailableReason ?? 'Save these numbers at their own address'}>
                <Camera className="h-3.5 w-3.5" /> Snapshot
              </button>
              <button type="button" {...btnSecondary()} onClick={() => setScheduleOpen(true)} disabled={!canPersist} title={unavailableReason ?? 'Send this report on a schedule'}>
                <CalendarClock className="h-3.5 w-3.5" /> Schedule
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 border-y border-[var(--rule)] py-3">
        {controlsExtra}
        <Field label="Range" htmlFor="report-range" className="min-w-[10rem]">
          <Select
            id="report-range"
            size="sm"
            value={range.key}
            onChange={(e) => {
              const key = e.target.value as RangeKey;
              if (key === 'cycle') setRange({ key, cycleId: cycles[0]?.id });
              else if (key === 'custom') setRange({ key, from: range.from, to: range.to });
              else setRange({ key });
            }}
            options={RANGE_OPTIONS.map((o) => ({ value: o.key, label: o.label, disabled: o.key === 'cycle' && cycles.length === 0 }))}
          />
        </Field>
        {range.key === 'cycle' && (
          <Field label="Cycle" htmlFor="report-cycle" className="min-w-[12rem]">
            <Select
              id="report-cycle"
              size="sm"
              value={range.cycleId ?? ''}
              onChange={(e) => setRange({ key: 'cycle', cycleId: e.target.value })}
              options={cycles.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
        )}
        {range.key === 'custom' && (
          <>
            <Field label="From" htmlFor="report-from">
              <TextInput id="report-from" type="date" size="sm" value={range.from ?? ''} onChange={(e) => setRange({ key: 'custom', from: e.target.value || undefined, to: range.to })} />
            </Field>
            <Field label="To" htmlFor="report-to">
              <TextInput id="report-to" type="date" size="sm" value={range.to ?? ''} onChange={(e) => setRange({ key: 'custom', from: range.from, to: e.target.value || undefined })} />
            </Field>
          </>
        )}
        {showSegment && (
          <Field label="Segment by" htmlFor="report-segment" className="min-w-[10rem]">
            <Select
              id="report-segment"
              size="sm"
              value={segment}
              onChange={(e) => setSegment(e.target.value as SegmentKey)}
              options={SEGMENT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Field>
        )}
        <p className="ml-auto self-center text-xs text-[var(--text-ter)]">
          <span className="font-mono uppercase tracking-[0.08em]">{resolved.label}</span>
          {segmentLabel ? <span> · by {segmentLabel}</span> : null}
          {rangeNote ? <span className="ml-2">{rangeNote}</span> : null}
        </p>
      </div>

      {children}

      <Modal
        open={snapshotOpen}
        onClose={() => setSnapshotOpen(false)}
        label="Snapshot"
        title={`Save ${name} as it is now`}
        description={`The numbers on this page, with ${resolved.label.toLowerCase()}${segmentLabel ? ` by ${segmentLabel.toLowerCase()}` : ''}, at their own address. Anyone signed in to this workspace can open it${justMe ? ', with this report’s name and search, even though the report itself is just yours' : ''}; there is no anonymous link.`}
        size="sm"
        footer={
          <>
            <button type="button" {...btnSecondary()} onClick={() => setSnapshotOpen(false)}>Cancel</button>
            <button type="button" {...btnPrimary()} onClick={() => void handleSnapshot()} disabled={saving || !snapshotName.trim()}>
              {saving ? 'Saving…' : 'Save snapshot'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" htmlFor="snapshot-name">
            <TextInput id="snapshot-name" autoFocus value={snapshotName} onChange={(e) => setSnapshotName(e.target.value)} placeholder={`${name} — ${new Date().toISOString().slice(0, 10)}`} />
          </Field>
          <Field label="Note (optional)" htmlFor="snapshot-note">
            <TextArea id="snapshot-note" rows={2} value={snapshotNote} onChange={(e) => setSnapshotNote(e.target.value)} placeholder="What this moment is for — a stand-up, a review, a before." />
          </Field>
        </div>
      </Modal>

      {summary && (
        <ScheduleReportDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} reportKey={reportKey} reportName={name} config={config} justMe={justMe} />
      )}
    </div>
  );
}
